/* @typescript-eslint/ban-ts-ignore */

import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import createFlightApi from 'api/flightOffset';
import createOrderApi, { Metadata, OrderItem } from 'api/order';
import { appContextSelector } from 'modules/app/appSlice';
import { defaultPortfolioSelector } from 'modules/projects/projectsSlice';
import { syncAttribution } from 'modules/user/attributionSlice';
import { currentUserSelector } from 'modules/user/userSlice';
import { AppThunk, RootState } from 'store';
import { setTag, tags } from 'utils/tags';
//region types

export type Steps = 'form' | 'customInputs' | 'summary' | 'error';

interface FirstStepPayload {
  from: AirportOption;
  to: AirportOption;
  via?: AirportOption;
  travellers: number;
  travelClass: TravelClass;
  roundTrip: boolean;
  flights?: number;
}

interface SetCurrencyPayload {
  currency: string;
}

interface EditProps {
  index: number;
  value: number;
}

interface EditTravelClassProps {
  index: number;
  value: string;
}

interface EditRoundTripProps {
  index: number;
  value: boolean;
}

interface SecondStepPayload {
  orderName: string;
  country: Option;
  department: string;
  mode?: FlightOffsetMode;
  channel?: OffsetChannel;
}

interface OrderPayload {
  number: string;
  date: string;
}

export interface FullOffsetFlight {
  flightLength: string;
  flightDistance: number;
  travelClass: TravelClass;
  roundTrip: boolean;
  travellers: number;
  kilosCo2: number;
  price: number;
}

interface State {
  fullOffsetFlights: FullOffsetFlight[];
  step: Steps;
  from?: AirportOption;
  to?: AirportOption;
  via?: AirportOption;
  travellers?: number;
  travelClass?: TravelClass;
  flights?: number;
  roundTrip?: boolean;
  orderName?: string;
  country?: Option;
  department?: string;
  price?: number;
  currency?: string;
  kiloCo2?: number;
  distance?: number;
  distanceFromVia?: number;
  distanceViaTo?: number;
  isLoading: boolean;
  orderNumber?: string;
  orderDate?: string;
  flightPax?: number;
  mode?: FlightOffsetMode;
  channel?: OffsetChannel;
}

interface OffsetWithPrice {
  price?: number;
  currency?: string;
  kiloCo2?: number;
  distance?: number;
  distanceFromVia?: number;
  distanceViaTo?: number;
  flightPax: number;
}

//endregion

const initialState: State = {
  fullOffsetFlights: [],
  step: 'form',
  isLoading: false,
};

const slice = createSlice({
  name: 'simpleOffsetFlight',
  initialState,
  reducers: {
    setFirstStep: (state: State, action: PayloadAction<FirstStepPayload>): State => {
      return { ...state, ...action.payload };
    },
    setSecondStep: (state: State, action: PayloadAction<SecondStepPayload>): State => {
      return { ...state, ...action.payload };
    },
    setOffsetWithPrice: (state: State, action: PayloadAction<OffsetWithPrice>): State => {
      return { ...state, ...action.payload };
    },
    reset: (): State => {
      return { ...initialState };
    },
    resetWithoutStep: (state: State): State => {
      return { ...initialState, step: state.step };
    },
    setStep: (state: State, action: PayloadAction<Steps>): State => {
      state.step = action.payload;
      return state;
    },
    setLoading: (state: State, action: PayloadAction<boolean>): State => {
      state.isLoading = action.payload;
      return state;
    },
    setOrder: (state: State, action: PayloadAction<OrderPayload>): State => {
      state.orderNumber = action.payload.number;
      state.orderDate = action.payload.date;
      return state;
    },

    setSalesCurrency: (state: State, action: PayloadAction<SetCurrencyPayload>): State => {
      return { ...state, ...action.payload };
    },

    addFlightRow: (state: State, action: PayloadAction<FullOffsetFlight>): State => {
      state.fullOffsetFlights.push(action.payload);
      return state;
    },

    updateFlightDistanceState: (state: State, action: PayloadAction<EditProps>): State => {
      const { index, value } = action.payload;
      const item = state.fullOffsetFlights[index];
      item.flightDistance = value;

      let flightLength = 'short';

      if (value > 1500 && value < 8000) {
        flightLength = 'medium';
      }

      if (value >= 8000 && value < 14000) {
        flightLength = 'long';
      }

      if (value >= 14000) {
        flightLength = 'ultra';
      }

      item.flightLength = flightLength;

      return state;
    },

    updateTravelClassState: (state: State, action: PayloadAction<EditTravelClassProps>): State => {
      const { index, value } = action.payload;
      const item = state.fullOffsetFlights[index];
      item.travelClass = value as TravelClass;
      return state;
    },

    updateRoundTripState: (state: State, action: PayloadAction<EditRoundTripProps>): State => {
      const { index, value } = action.payload;
      const item = state.fullOffsetFlights[index];
      item.roundTrip = value;
      return state;
    },

    updateTravellersState: (state: State, action: PayloadAction<EditProps>): State => {
      const { index, value } = action.payload;
      const item = state.fullOffsetFlights[index];
      item.travellers = value;
      return state;
    },
    updateFlightPriceState: (state: State, action: PayloadAction<EditProps>): State => {
      const { index, value } = action.payload;
      const item = state.fullOffsetFlights[index];
      item.price = value;
      return state;
    },
    updateFlightOffsetState: (state: State, action: PayloadAction<EditProps>): State => {
      const { index, value } = action.payload;
      const item = state.fullOffsetFlights[index];
      item.kilosCo2 = value;
      return state;
    },
    removeFlightRow: (state: State, action: PayloadAction<number>): State => {
      state.fullOffsetFlights.splice(action.payload, 1);
      return state;
    },
  },
});

const {
  setFirstStep,
  setSecondStep,
  reset,
  setStep,
  setLoading,
  setOrder,
  resetWithoutStep,
  setOffsetWithPrice,
  addFlightRow,
  updateFlightDistanceState,
  updateTravelClassState,
  updateRoundTripState,
  updateTravellersState,
  updateFlightPriceState,
  updateFlightOffsetState,
  removeFlightRow,
  setSalesCurrency,
} = slice.actions;
export { setFirstStep, setSecondStep, reset, setStep, removeFlightRow };
export default slice.reducer;

interface CalculateFlightOffsetParams {
  from: string;
  to: string;
  via?: string;
  travelClass: TravelClass;
  travellers: number;
  flights: number;
  roundTrip: boolean;
}

export const calculateFlightOffset = (params: CalculateFlightOffsetParams): AppThunk => async (dispatch, getState): Promise<void> => {
  dispatch(setLoading(true));
  const currentUser = currentUserSelector(getState());
  const context = appContextSelector(getState());
  const flightOffsetApi = createFlightApi(context);
  const defaultPortfolio = defaultPortfolioSelector(getState());

  let offset;
  let partialOffsetFromVia;
  let partialOffsetViaTo;
  try {
    if (params.via) {
      partialOffsetFromVia = await flightOffsetApi.getFlightOffset({ ...params, from: params.from, to: params.via });
      partialOffsetViaTo = await flightOffsetApi.getFlightOffset({ ...params, from: params.via, to: params.to });

      offset = {
        kilosCo2e:
          partialOffsetFromVia.kilosCo2e && partialOffsetViaTo.kilosCo2e
            ? partialOffsetFromVia?.kilosCo2e + partialOffsetViaTo?.kilosCo2e
            : undefined,
        kilosCo2: partialOffsetFromVia.kilosCo2 + partialOffsetViaTo.kilosCo2,
        distanceKm: partialOffsetFromVia.distanceKm + partialOffsetViaTo.distanceKm,
      };
    } else {
      offset = await flightOffsetApi.getFlightOffset(params);
    }

    let multiplicator = params.roundTrip ? 2 : 1;
    const kilosCo2 = offset.kilosCo2e ?? offset.kilosCo2;

    multiplicator = params.via ? multiplicator * 2 : multiplicator;
    const flightPax = multiplicator * Number(params.flights || 1) * Number(params.travellers ?? 1);
    const price = await flightOffsetApi.getFlightOffsetPrice(
      { kilosCo2: kilosCo2, flightPax, flightDistanceKm: offset.distanceKm },
      currentUser?.customerId ?? '',
      defaultPortfolio?.portfolioData.id ?? '',
    );

    dispatch(
      setOffsetWithPrice({
        price: price.salesPrice,
        currency: price.salesCurrency,
        kiloCo2: kilosCo2,
        distance: offset.distanceKm,
        distanceFromVia: partialOffsetFromVia?.distanceKm,
        distanceViaTo: partialOffsetViaTo?.distanceKm,
        flightPax,
      }),
    );
    dispatch(setLoading(false));
    dispatch(setStep('customInputs'));
  } catch (err) {
    dispatch(setLoading(false));
    dispatch(setStep('error'));
    dispatch(resetWithoutStep());
  }
};

const saveUsedAirports = (usedAirports: AirportOption[]): AppThunk => (): void => {
  try {
    let savedAirports = JSON.parse(localStorage.getItem('usedAirports') ?? '[]') as AirportOption[];
    savedAirports = savedAirports.filter(s => usedAirports.findIndex(u => u.value === s.value) === -1);
    savedAirports.unshift(...usedAirports);
    const maxSavedAirports = 5;
    savedAirports = savedAirports.slice(0, maxSavedAirports);
    localStorage.setItem('usedAirports', JSON.stringify(savedAirports));
  } catch {
    //err
  }
};

function generateOrderItemsForByDistanceMode(flights: FullOffsetFlight[], portfolioId: string): OrderItem[] {
  return flights.map(f => ({
    type: 'impact',
    impact: 'Co2',
    kilosCo2: f.kilosCo2 ?? 0,
    price: f.price ?? 0,
    portfolioId,
    details: {
      flight: {
        flights: 1,
        passengers: Number(f.travellers ?? 0),
        roundTrip: f.roundTrip ?? false,
        travelClass: f.travelClass ?? ('economy' as TravelClass),
        routeLegs: [
          {
            distanceKm: f.flightDistance ?? 0,
          },
        ],
      },
    },
    feeFactors: {
      flightPax: f.roundTrip ? 2 * Number(f.travellers ?? 1) : Number(f.travellers ?? 1),
    },
  }));
}

function generateOrderItemForFromToMode(flight: State, portfolioId: string): OrderItem[] {
  const routeLegs: RouteLegs[] = [];

  if (flight.via) {
    routeLegs.push({
      distanceKm: flight.distanceFromVia ?? 0,
      fromAirportCode: flight.from?.airportCode ?? '',
      toAirportCode: flight.via?.airportCode ?? '',
      fromCountryName: flight.from?.countryCode ?? '',
      toCountryName: flight.via?.countryCode ?? '',
      fromCityName: flight.from?.city ?? '',
      toCityName: flight.via?.city ?? '',
    });
    routeLegs.push({
      distanceKm: flight.distanceViaTo ?? 0,
      fromAirportCode: flight.via?.airportCode ?? '',
      toAirportCode: flight.to?.airportCode ?? '',
      fromCountryName: flight.via?.countryCode ?? '',
      toCountryName: flight.to?.countryCode ?? '',
      fromCityName: flight.via?.city ?? '',
      toCityName: flight.to?.city ?? '',
    });
  } else {
    routeLegs.push({
      distanceKm: flight.distance ?? 0,
      fromAirportCode: flight.from?.airportCode ?? '',
      toAirportCode: flight.to?.airportCode ?? '',
      fromCountryName: flight.from?.countryCode ?? '',
      toCountryName: flight.to?.countryCode ?? '',
      fromCityName: flight.from?.city ?? '',
      toCityName: flight.to?.city ?? '',
    });
  }

  return [
    {
      type: 'impact',
      impact: 'Co2',
      kilosCo2: flight.kiloCo2 ?? 0,
      price: flight.price ?? 0,
      portfolioId,
      details: {
        flight: {
          flights: flight.via ? 2 * Number(flight.flights ?? 1) : Number(flight.flights ?? 1),
          passengers: Number(flight.travellers ?? 0),
          roundTrip: flight.roundTrip ?? false,
          travelClass: flight.travelClass ?? ('economy' as TravelClass),
          routeLegs,
        },
      },
      feeFactors: {
        flightPax: flight.flightPax ?? 0,
      },
    },
  ];
}

function generateMetadata(flight: State): Metadata {
  const customMetadata: {
    internalName: string;
    value: string;
  }[] = [];

  if (flight.country) {
    customMetadata.push({
      internalName: 'country',
      value: flight.country.value,
    });
  }

  if (flight.department) {
    customMetadata.push({
      internalName: 'department',
      value: flight.department,
    });
  }

  let flightMetadata;
  if (flight.mode === 'from/to') {
    flightMetadata = {
      travelersCount: Number(flight.travellers ?? 0),
      roundtrip: flight.roundTrip ?? false,
      distanceKm: flight.distance ?? 0,
      fromAirport: flight.from?.airportCode ?? '',
      toAirport: flight.to?.airportCode ?? '',
      fromCountry: flight.from?.countryCode ?? '',
      toCountry: flight.to?.countryCode ?? '',
      fromCity: flight.from?.city ?? '',
      toCity: flight.to?.city ?? '',
      flights: flight.via ? 2 * Number(flight.flights ?? 1) : Number(flight.flights ?? 1),
      passengers: Number(flight.travellers ?? 0),
      travelClass: flight.travelClass ?? ('economy' as TravelClass),
    };
  }

  if (flight.mode === 'byDistance') {
    flightMetadata = {
      travelersCount: flight.fullOffsetFlights.reduce((prev, curr) => prev + curr.travellers, 0),
      distanceKm: flight.fullOffsetFlights.reduce((prev, curr) => prev + curr.flightDistance, 0),
      flights: flight.fullOffsetFlights.length,
      passengers: flight.fullOffsetFlights.reduce((prev, curr) => prev + curr.travellers, 0),
    };
  }

  return {
    //@ts-ignore
    flight: flightMetadata,
    order: {
      channel: flight.channel ?? '',
    },
    custom: customMetadata,
  };
}

function getOrderItems(flight: State, portfolioId: string): OrderItem[] {
  switch (flight.mode) {
    case 'from/to':
      return generateOrderItemForFromToMode(flight, portfolioId);
    case 'byDistance':
      return generateOrderItemsForByDistanceMode(flight.fullOffsetFlights, portfolioId);
    default:
      return [];
  }
}

export const createOrder = (cb: CallableFunction, mode: 'quick' | 'full' = 'full'): AppThunk => async (dispatch, getState): Promise<void> => {
  const { simpleFlightOffset: flightOffset } = getState();
  const currentUser = currentUserSelector(getState());
  const context = appContextSelector(getState());
  const orderApi = createOrderApi(context);
  const defaultPortfolio = defaultPortfolioSelector(getState());
  dispatch(setLoading(true));
  try {
    const metadata = generateMetadata(flightOffset);
    const items = getOrderItems(flightOffset, defaultPortfolio?.portfolioData.id ?? '');

    const orderResult = await orderApi.create(
      {
        description: flightOffset.orderName ?? '',
        comment: '',
        currency: flightOffset.currency ?? '',
        category: 'Flights',
        items,
        payment: {
          type: 'invoice',
        },
        metadata,
      },
      currentUser?.customerId ?? '',
    );
    setTag({
      ...(mode === 'quick' ? tags.portalQuickOffset.placeOrder : tags.portalOffset.placeOrder),
      categories: [orderResult.order.category],
      transactionId: orderResult.orderId,
      transactionTotal: orderResult.order.totalPrice,
      currencyCode: orderResult.order.currency,
      transactionProducts: orderResult.order.items.map(item => ({
        category: orderResult.order.category,
        name: 'Flight offset',
        price: item.price,
        quantity: item.kilosCo2,
        sku: 'flight-offset',
      })),
    });
    dispatch(setLoading(false));
    dispatch(
      setOrder({
        number: orderResult.order.choooseId,
        date: orderResult.order.createdDate,
      }),
    );
    dispatch(syncAttribution());

    if (flightOffset.from && flightOffset.to) {
      if (flightOffset.via) {
        dispatch(saveUsedAirports([flightOffset.from, flightOffset.via, flightOffset.to]));
      } else {
        dispatch(saveUsedAirports([flightOffset.from, flightOffset.to]));
      }
    }
    cb();
  } catch (err) {
    dispatch(setLoading(false));
    dispatch(setStep('error'));
    dispatch(resetWithoutStep());
  }
};

export const addNewFlightItem = (props?: FullOffsetFlight): AppThunk => async (dispatch, getState): Promise<void> => {
  dispatch(setLoading(true));
  const currentUser = currentUserSelector(getState());
  const context = appContextSelector(getState());
  const flightOffsetApi = createFlightApi(context);
  const defaultPortfolio = defaultPortfolioSelector(getState());

  const item: FullOffsetFlight = {
    flightLength: 'short',
    flightDistance: 1500,
    travelClass: 'economy' as TravelClass,
    roundTrip: false,
    travellers: 1,
    price: 0,
    kilosCo2: 0,
  };

  try {
    const result = await flightOffsetApi.getFlightOffset({
      travellers: item.travellers,
      distance: item.flightDistance,
      travelClass: 'economy' as TravelClass,
      roundTrip: item.roundTrip,
    });
    const kilosCo2 = result.kilosCo2e ?? result.kilosCo2;
    const flightPax = Number(item.travellers ?? 1) * (item.roundTrip ? 2 : 1);
    const flightPrice = await flightOffsetApi.getFlightOffsetPrice(
      { kilosCo2, flightPax, flightDistanceKm: item.flightDistance, flight: true },
      currentUser?.customerId ?? '',
      defaultPortfolio?.portfolioData.id ?? '',
    );

    item.kilosCo2 = kilosCo2;
    item.price = flightPrice.salesPrice;

    dispatch(setSalesCurrency({ currency: flightPrice.salesCurrency }));
    dispatch(addFlightRow({ ...props, ...item }));
    dispatch(setLoading(false));
  } catch {
    dispatch(setLoading(false));
  }
};

export const updateFlightDistance = (props: EditProps): AppThunk => async (dispatch, getState): Promise<void> => {
  const { index, value } = props;
  dispatch(setLoading(true));
  const currentUser = currentUserSelector(getState());
  const context = appContextSelector(getState());
  const flightOffsetApi = createFlightApi(context);
  const { fullOffsetFlights } = getState().simpleFlightOffset;
  const item = fullOffsetFlights[index];
  const defaultPortfolio = defaultPortfolioSelector(getState());

  try {
    const result = await flightOffsetApi.getFlightOffset({
      travellers: item.travellers,
      distance: value,
      travelClass: item.travelClass,
      roundTrip: item.roundTrip,
    });

    const kilosCo2 = result.kilosCo2e ?? result.kilosCo2;
    const flightPax = Number(item.travellers ?? 1) * (item.roundTrip ? 2 : 1);
    const flightPrice = await flightOffsetApi.getFlightOffsetPrice(
      { kilosCo2, flightPax, flightDistanceKm: value, flight: true },
      currentUser?.customerId ?? '',
      defaultPortfolio?.portfolioData.id ?? '',
    );

    dispatch(updateFlightOffsetState({ index, value: kilosCo2 }));
    dispatch(updateFlightPriceState({ index, value: flightPrice.salesPrice }));
    dispatch(updateFlightDistanceState(props));
    dispatch(setLoading(false));
  } catch {
    dispatch(setLoading(false));
  }
};

export const updateTravelClass = (props: EditTravelClassProps): AppThunk => async (dispatch, getState): Promise<void> => {
  const { index, value } = props;
  dispatch(setLoading(true));
  const currentUser = currentUserSelector(getState());
  const context = appContextSelector(getState());
  const flightOffsetApi = createFlightApi(context);
  const { fullOffsetFlights } = getState().simpleFlightOffset;
  const item = fullOffsetFlights[index];
  const defaultPortfolio = defaultPortfolioSelector(getState());

  try {
    const result = await flightOffsetApi.getFlightOffset({
      travellers: item.travellers,
      distance: item.flightDistance,
      travelClass: value as TravelClass,
      roundTrip: item.roundTrip,
    });
    const kilosCo2 = result.kilosCo2e ?? result.kilosCo2;
    const flightPax = Number(item.travellers ?? 1) * (item.roundTrip ? 2 : 1);
    const flightPrice = await flightOffsetApi.getFlightOffsetPrice(
      { kilosCo2, flightPax, flightDistanceKm: item.flightDistance, flight: true },
      currentUser?.customerId ?? '',
      defaultPortfolio?.portfolioData.id ?? '',
    );

    dispatch(updateFlightOffsetState({ index, value: kilosCo2 }));
    dispatch(updateFlightPriceState({ index, value: flightPrice.salesPrice }));
    dispatch(updateTravelClassState(props));
    dispatch(setLoading(false));
  } catch {
    dispatch(setLoading(false));
  }
};
export const updateRoundTrip = (props: EditRoundTripProps): AppThunk => async (dispatch, getState): Promise<void> => {
  const { index, value } = props;
  dispatch(setLoading(true));
  const currentUser = currentUserSelector(getState());
  const context = appContextSelector(getState());
  const flightOffsetApi = createFlightApi(context);

  const { fullOffsetFlights } = getState().simpleFlightOffset;
  const item = fullOffsetFlights[index];
  const defaultPortfolio = defaultPortfolioSelector(getState());

  try {
    const result = await flightOffsetApi.getFlightOffset({
      travellers: item.travellers,
      distance: item.flightDistance,
      travelClass: item.travelClass,
      roundTrip: value,
    });
    const kilosCo2 = result.kilosCo2e ?? result.kilosCo2;
    const flightPax = Number(item.travellers ?? 1) * (value ? 2 : 1);
    const flightPrice = await flightOffsetApi.getFlightOffsetPrice(
      { kilosCo2, flightPax, flightDistanceKm: item.flightDistance, flight: true },
      currentUser?.customerId ?? '',
      defaultPortfolio?.portfolioData.id ?? '',
    );

    dispatch(updateFlightOffsetState({ index, value: kilosCo2 }));
    dispatch(updateFlightPriceState({ index, value: flightPrice.salesPrice }));
    dispatch(updateRoundTripState(props));
    dispatch(setLoading(false));
  } catch {
    dispatch(setLoading(false));
  }
};

export const updateTravellers = (props: EditProps): AppThunk => async (dispatch, getState): Promise<void> => {
  const { index, value } = props;
  dispatch(setLoading(true));
  const currentUser = currentUserSelector(getState());
  const context = appContextSelector(getState());
  const flightOffsetApi = createFlightApi(context);
  const { fullOffsetFlights } = getState().simpleFlightOffset;
  const item = fullOffsetFlights[index];
  const defaultPortfolio = defaultPortfolioSelector(getState());

  try {
    const result = await flightOffsetApi.getFlightOffset({
      travellers: value,
      distance: item.flightDistance,
      travelClass: item.travelClass,
      roundTrip: item.roundTrip,
    });
    const kilosCo2 = result.kilosCo2e ?? result.kilosCo2;
    const flightPax = Number(value ?? 1) * (item.roundTrip ? 2 : 1);
    const flightPrice = await flightOffsetApi.getFlightOffsetPrice(
      { kilosCo2, flightPax, flightDistanceKm: item.flightDistance, flight: true },
      currentUser?.customerId ?? '',
      defaultPortfolio?.portfolioData.id ?? '',
    );

    dispatch(updateFlightOffsetState({ index, value: kilosCo2 }));
    dispatch(updateFlightPriceState({ index, value: flightPrice.salesPrice }));
    dispatch(updateTravellersState(props));
    dispatch(setLoading(false));
  } catch {
    dispatch(setLoading(false));
  }
};

export const getFullFlightKilosCo2 = createSelector(
  (state: RootState) => state.simpleFlightOffset.fullOffsetFlights,
  flightOffsets => flightOffsets.reduce((prev, curr) => prev + curr.kilosCo2, 0),
);

export const getFullFlightPrice = createSelector(
  (state: RootState) => state.simpleFlightOffset.fullOffsetFlights,
  flightOffsets => flightOffsets.reduce((prev, curr) => prev + curr.price, 0),
);
